/**
 * **再現エンジンの盤面 → `duelEditor` の盤面の状態**（Issue #523）。**`duelEditor/` の外**。
 *
 * `duelEditor/core/board` が受け取るのは「**ゾーンのキー → 置いてあるもの**」の表だけで、
 * 再現エンジンの形は知らない（`core/field` が `overlay_zones` の形を知らないのと同じ）。
 * **その形を知っているのはこのファイルだけ。**
 *
 * 入口は 2 つ:
 *
 * 1. **状態チャンネルの盤面**（時系列。`editorPorts/boardTimeline.js` の形）
 *    `{ players: [{ hand: [uid], mzone: [uid|null ×7], szone: [uid|null ×8], deck: n, extra: n }],
 *       cards: { uid: { code, pos } }, stats: { uid: { atk, def, level } } }`
 * 2. **生の board**（1 枚。`duel_info()` 互換）—— イベントがまだ無い replay の逃げ道
 *
 * どちらも **uid を落とさない**（演出がカードを指すのに要る。今までの写し替えはここで落としていた）。
 *
 * **表示形式（`defense`）はモンスターだけ**（Issue #523）—— core の位置はセットした魔法・罠も
 * `POS_FACEDOWN`（＝裏攻撃 | **裏守備**）なので、そのまま `isDefense` に通すと
 * **伏せカードが 90° 寝る**。魔法・罠に表示形式は無い。
 */
import { isDefense, isFaceUp } from './ocgPosition';
import { toBoardState } from '../duelEditor/core/board';
import { zoneKey } from '../duelEditor/core/field/zones';

/** 空の枠（`duel_info()` の古い形） */
const EMPTY_UID = '00000000';

const MAIN_ROW = 5;

/** 山（キー、1 人ぶんの場所）の種類 */
const put = (zones, key, value) => { if (key && value != null) zones[key] = value; };

/* ---- (1) 状態チャンネルの盤面（uid の配列） ---- */

const fromSteps = (state) => {
  const zones = {};
  const cards = state.cards ?? {};
  const stats = state.stats ?? {};
  /** @param uid カードの uid @param monster モンスターの場所か（表示形式があるのはここだけ） */
  const card = (uid, monster = false) => {
    if (!uid) return null;
    const c = cards[uid] ?? {};
    const code = c.code ?? null;
    /*
     * **中身の分からないカード**（観戦の視点でルームから届く `code: null`・`hidden: true`。Issue #766）は裏（スリーブ）で描く。
     * 置いてあるカードではあるので空のゾーン（`EMPTY_UID`・`DummyCardInfo`）とは別 —— uid は持ったまま（`code: null` で表す。
     * `core/board` の `Card.code` は「裏側で分からなければ null」）。表示形式（守備で寝る）は公開の情報なのでそのまま。
     */
    const hidden = c.hidden === true || code == null;
    return {
      uid,
      code: hidden ? null : code,
      faceUp: !hidden && isFaceUp(c.pos ?? 0),
      defense: monster && isDefense(c.pos ?? 0),
      stats: stats[uid] ?? null,
    };
  };
  [0, 1].forEach((p) => {
    const pl = state.players?.[p] ?? {};
    (pl.mzone ?? []).slice(0, MAIN_ROW).forEach((uid, i) => put(zones, zoneKey('monster', p, i), card(uid, true)));
    (pl.szone ?? []).slice(0, MAIN_ROW).forEach((uid, i) => put(zones, zoneKey('magic_trap', p, i), card(uid)));
    put(zones, zoneKey('field', p), card((pl.szone ?? [])[MAIN_ROW]));
    [['hand', pl.hand], ['grave', pl.grave], ['removed', pl.removed]].forEach(([kind, list]) => {
      const pile = (list ?? []).map(card).filter(Boolean);
      if (pile.length) put(zones, zoneKey(kind, p), pile);
    });
    // デッキと EX デッキは**枚数だけ**（core はアノテーションで決まるまで中身を知らない）
    put(zones, zoneKey('maindeck', p), pl.deck || null);
    put(zones, zoneKey('extra', p), pl.extra || null);
  });
  return zones;
};

/* ---- (2) 生の board（`duel_info()` 互換） ---- */

const fromRaw = (board) => {
  const zones = {};
  const card = (raw, monster = false) => {
    if (!raw || raw.unique_id === EMPTY_UID) return null;
    return {
      uid: raw.unique_id ?? null,
      code: raw.card_id ?? null,
      faceUp: raw.is_face_up !== false,
      defense: monster && raw.battle_pos != null && raw.battle_pos !== 'attack',
      stats: null,
    };
  };
  [0, 1].forEach((p) => {
    (board.monster_field?.[p] ?? []).slice(0, MAIN_ROW).forEach((c, i) => put(zones, zoneKey('monster', p, i), card(c, true)));
    (board.magic_trap?.[p] ?? []).slice(0, MAIN_ROW).forEach((c, i) => put(zones, zoneKey('magic_trap', p, i), card(c)));
    put(zones, zoneKey('field', p), card((board.magic_trap?.[p] ?? [])[MAIN_ROW]));
    [['hand', board.hand], ['grave', board.grave], ['removed', board.removed]].forEach(([kind, per]) => {
      const pile = (per?.[p] ?? []).map(card).filter(Boolean);
      if (pile.length) put(zones, zoneKey(kind, p), pile);
    });
    put(zones, zoneKey('maindeck', p), (board.maindeck?.[p] ?? []).length || null);
    put(zones, zoneKey('extra', p), (board.extra?.[p] ?? []).length || null);
  });
  return zones;
};

/**
 * 盤面の状態を作る。状態チャンネルがあればそちら、無ければ生の board（1 枚）。
 * どちらも無ければ `known: false` の盤面（**null を返さない**）。
 *
 * @param state 状態チャンネルの盤面（`frame.states.board`）
 * @param raw 生の board（`OcgGetState` の `board`）
 */
export function toDuelBoard(state = null, raw = null) {
  if (state?.players) return toBoardState({ zones: fromSteps(state) });
  if (raw?.monster_field) return toBoardState({ zones: fromRaw(raw) });
  return toBoardState();
}

/** 手札の枚数（HUD が読む） */
export const handCounts = (board) => [0, 1].map((p) => board?.count?.(zoneKey('hand', p)) ?? 0);
